/**
 * Parallax Layers — BG / MID / FG with different speeds for a soft camera glide.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
  pickMediaSrc,
} from "../../reel/sceneShared.js";

export const parallaxLayers: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = (inputs.text ?? "Bewegung,\ndie verbindet.").split("\n");
  const sub = inputs.subtext ?? "";
  const tag = inputs.number ?? "FEATURE";
  const bg = pickMediaSrc(inputs.media);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const bgId = `${clipPrefix}-bg-layer`;
  const midId = `${clipPrefix}-mid`;
  const fgId = `${clipPrefix}-fg`;
  const tagId = `${clipPrefix}-tag`;
  const headIds = headline.map((_, i) => `${clipPrefix}-h-${i}`);
  const subId = `${clipPrefix}-sub`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-canvas`, { variant: "cinematic", accent });

  const bgLayer = bg
    ? `<div id="${bgId}" style="position:absolute;inset:-40px;background:url('${escapeHtml(bg)}') center/cover no-repeat;filter:blur(2px) brightness(0.55);"></div>`
    : `<div id="${bgId}" style="position:absolute;inset:-40px;background:radial-gradient(circle at 30% 40%, ${withAlpha(accent, 0.4)} 0%, transparent 60%), radial-gradient(circle at 70% 70%, ${withAlpha(accent, 0.25)} 0%, transparent 60%), ${themeColors.bg};"></div>`;

  const mid = `<div id="${midId}" style="position:absolute;inset:0;display:flex;align-items:flex-end;justify-content:flex-start;padding:80px;">
    <svg width="${ctx.format === "9:16" ? 1080 : 1920}" height="600" viewBox="0 0 1920 600" style="position:absolute;left:-100px;bottom:0;width:120%;height:auto;opacity:0.6;">
      <path d="M0 400 Q480 200 960 360 T1920 320 V600 H0 Z" fill="${withAlpha(accent, 0.32)}"/>
      <path d="M0 460 Q480 320 960 420 T1920 380 V600 H0 Z" fill="${withAlpha(accent, 0.55)}"/>
    </svg>
  </div>`;

  const fg = `<div id="${fgId}" style="position:relative;display:flex;flex-direction:column;gap:24px;padding:80px;max-width:${ctx.format === "9:16" ? 880 : 1200}px;">
    ${tag ? `<div id="${tagId}" style="display:inline-flex;align-self:flex-start;padding:8px 18px;border-radius:9999px;background:${withAlpha(accent, 0.18)};border:1.5px solid ${withAlpha(accent, 0.45)};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.7}px;letter-spacing:4px;text-transform:uppercase;color:${accent};">${escapeHtml(tag)}</div>` : ""}
    ${headline.map((line, i) => `<div id="${headIds[i]}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.88}px;letter-spacing:-2.5px;color:${themeColors.fg};line-height:0.98;text-wrap:balance;">${escapeHtml(line)}</div>`).join("")}
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${md * 0.55}px;color:${withAlpha(themeColors.fg, 0.78)};max-width:680px;line-height:1.35;text-wrap:balance;">${escapeHtml(sub)}</div>` : ""}
  </div>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
    ${bgLayer}
    ${mid}
    ${fg}
  </div>`;

  const init = [
    gsapSetLine(`#${bgId}`, { scale: 1.0, x: 0, y: 0 }),
    gsapSetLine(`#${midId}`, { x: 0, y: 0 }),
    gsapSetLine(`#${fgId}`, { x: 0, y: 0 }),
    tag ? gsapSetLine(`#${tagId}`, { opacity: 0, y: -10 }) : "",
    ...headIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 28 })),
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 14 }) : "",
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${bgId}`, { x: -30, y: -10, scale: 1.08, duration: clipDurationSec, ease: "raw:\"sine.inOut\"" }, 0.0),
    t.to(`#${midId}`, { x: 60, duration: clipDurationSec, ease: "raw:\"sine.inOut\"" }, 0.0),
    t.to(`#${fgId}`, { x: -20, duration: clipDurationSec, ease: "raw:\"sine.inOut\"" }, 0.0),
  ];
  if (tag) tweens.push(t.to(`#${tagId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.1));
  headIds.forEach((id, i) => tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, duration: 0.7, ease: "raw:\"power3.out\"" }, 0.25 + i * 0.18)));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.4 + headIds.length * 0.18));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
